import type { ReactNode } from 'react';
import { Button, ErrorText, Field, Spinner } from '~/ui/primitives';
import { StepHeading } from './StepHeading';

/**
 * Step 1 of the email → OTP flow. Collects the address (and, in registration,
 * an optional display name) and asks the server to send a code. `POST
 * /v1/accounts/email/start` answers identically whether or not the address is
 * already registered, so nothing here can be used to discover who has a
 * wallet — a conflict is disclosed only at step 2, to someone who has proven
 * they control the mailbox.
 *
 * Prop-driven, and used by TWO callers: `/register` (create an account) and
 * the Settings nudge (bind an address to an account that has none). The
 * differences between them are copy and the name field, which is why those
 * are props with registration's wording as the default — the alternative was
 * a second copy of this component and of the state machine around it, which
 * is how the two would drift.
 *
 * The name field renders only when `onNameChange` is supplied: binding an
 * address to an existing account does not touch the display name, and a
 * disabled or ignored box would be a control that does nothing.
 *
 * `headingLevel` exists because the two callers sit at different depths in
 * the document. `/register` renders this as the page's own content, so its
 * title is the `<h1>`. Settings renders it INSIDE a card on a page that
 * already has an `<h1>` ("Settings"), where a second `<h1>` is both an
 * accessibility defect — one document, two top-level headings — and a visual
 * one, a 24px display face beside sibling cards' 16px `<h2>`. Defaults to 1,
 * so registration is untouched.
 */
export function EmailStep({
  title = 'Create a new SCP Wallet',
  headingLevel = 1,
  headingId,
  autoFocus = false,
  description = "We'll email you a code to confirm the address, then secure the wallet with this device's passkey.",
  submitLabel = 'Continue',
  name,
  email,
  onNameChange,
  onEmailChange,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  busy,
  error,
}: {
  title?: string;
  /** 1 (default) for a page-level step; 2 when rendered inside a card. */
  headingLevel?: 1 | 2;
  /** DOM id for the heading, so a `role="dialog"` caller can `aria-labelledby` it. */
  headingId?: string;
  /**
   * Focus the ADDRESS input on mount — not the optional name field above it,
   * which is the first input in DOM order but not the thing being asked for.
   * Default `false`, so the two page-level callers keep the browser's own
   * behaviour; the embedded prompt sets it because a dialog that opens with
   * focus still on `<body>` is never announced.
   *
   * GUARDED ELSEWHERE: this prop is accessibility behaviour, and its only
   * coverage lives in `src/popup/components/__tests__/RegisterPrompt.test.tsx`
   * ("opens with focus on the control being asked for") — there is no
   * `EmailStep.test.tsx`. Moving or replacing the field below silently costs
   * that dialog its initial focus and its announcement, and the test that
   * catches it is in another component's file.
   */
  autoFocus?: boolean;
  description?: ReactNode;
  submitLabel?: string;
  name?: string;
  email: string;
  onNameChange?: (v: string) => void;
  onEmailChange: (v: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  busy: boolean;
  error: string | null;
}) {
  const canSubmit = email.trim().length > 0 && !busy;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
        <StepHeading level={headingLevel} id={headingId}>
          {title}
        </StepHeading>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{description}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {onNameChange && (
          <Field
            label="Name (optional)"
            value={name ?? ''}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Alex Müller"
            autoComplete="name"
          />
        )}
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus={autoFocus}
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onSubmit();
          }}
          placeholder="e.g. alex@example.com"
        />
        <Button onClick={onSubmit} disabled={!canSubmit}>
          {busy ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            submitLabel
          )}
        </Button>
        {onCancel && (
          <div>
            <Button variant="tertiary" size="sm" fullWidth={false} onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          </div>
        )}
      </div>
      {error && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <ErrorText>{error}</ErrorText>
        </div>
      )}
    </>
  );
}
